import { queryOptions, useQuery, useSuspenseQueries } from '@tanstack/react-query'
import { HailDataAvailabilitySchema, LightningDataAvailabilitySchema, ListReportTypesResultSchema } from '@xweather/protect-types'
import { useMemo } from 'react'
import { useFlags } from '../../launchdarkly.ts'
import { asQueryParam, type Impersonation, useImpersonation } from '../impersonation.ts'
import { useSubscription } from '../service.ts'
import { AnalyzeApi } from './api.ts'
import { localLightningReportTypeRules } from './localReportTypeRules.ts'

const reportTypesQuery = (impersonation: Impersonation | undefined) =>
	queryOptions({
		queryKey: ['ANALYZE_REPORT_TYPES', impersonation?.subscriptionId],
		staleTime: 1000 * 60 * 10,
		queryFn: async () =>
			ListReportTypesResultSchema.parse(
				await AnalyzeApi.get('api/v2/analyze/report-types', { searchParams: asQueryParam(impersonation) }).json(),
			).reportTypes,
	})

const useReportTypes = (enabled: boolean) => {
	const impersonation = useImpersonation()
	const queries: ReturnType<typeof reportTypesQuery>[] = enabled ? [reportTypesQuery(impersonation)] : []
	const [result] = useSuspenseQueries({ queries })
	return result?.data
}

export const useLightningReportTypeRules = () => {
	const { analyzeV2LightningReports, analyzeLightningReportTypeRules } = useFlags()
	const fromEndpoint = !!analyzeV2LightningReports || !!analyzeLightningReportTypeRules
	const { features } = useSubscription().data
	const reportTypes = useReportTypes(fromEndpoint)

	return useMemo(() => reportTypes?.LIGHTNING ?? localLightningReportTypeRules(features), [reportTypes, features])
}

export const useHailReportTypeRules = () => useReportTypes(!!useFlags().analyzeHailReports)?.HAIL

const hailDataAvailabilityQuery = (impersonation: Impersonation | undefined) =>
	queryOptions({
		queryKey: ['ANALYZE_DATA_AVAILABILITY', 'HAIL', impersonation?.subscriptionId],
		refetchInterval: 1000 * 60,
		queryFn: async () =>
			HailDataAvailabilitySchema.parse(
				await AnalyzeApi.get('api/v2/analyze/report-types/HAIL/data-availability', { searchParams: asQueryParam(impersonation) }).json(),
			),
	})

export const getLightningDataAvailability = async (impersonation: Impersonation | undefined) =>
	LightningDataAvailabilitySchema.parse(
		await AnalyzeApi.get('api/v2/analyze/report-types/LIGHTNING/data-availability', { searchParams: asQueryParam(impersonation) }).json(),
	)

export const useHailDataAvailability = () => {
	const enabled = !!useFlags().analyzeHailReports
	const impersonation = useImpersonation()
	return useQuery({ ...hailDataAvailabilityQuery(impersonation), enabled })
}
